import React, {
  createContext,
  FC,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { ApolloClient, InMemoryCache, useApolloClient } from '@apollo/client';
import { Auth0Provider } from '@auth0/auth0-react';
import { newIORef } from 'fp-ts/lib/IORef';
import * as O from 'fp-ts/Option';
import { useAsyncResource } from 'use-async-resource';

import { useSitecoreLayout } from './useSitecoreLayout';
import { useSiteCoreUserType } from './useSiteCoreUserType';

import { User } from '@domain/auth';

import { login, logout, useUserTrackingEvent } from '@use-cases/auth';
import { useNavigateLanguage } from '@use-cases/language-negotiation';

import { useOktaWidget } from '@repositories/auth/hooks';

import { FEATURE_AUTH0_LOGIN, isEnabled } from '@utils/features';

import Loading from '@domui-components/Loading';
import {
  AppConfig,
  AppConfigContextType,
  defaultConfig,
} from '@domui-domain/appConfig';
import { useDominoUserAccount } from '@domui-hooks/useDominoUserAccount';
import { getAppConfig } from '@domui-repositories/appConfig';

import { useTranslation } from '@external/react-i18next';
import ErrorBoundary from '@hooks/error-boundary';

const AppConfigContext = createContext<AppConfigContextType>({
  ...defaultConfig,
  oktaWidget: {
    usernameUsedForLogin: newIORef<O.Option<string>>(O.none)(),
    clearTokens: () => {},
    setTokens: () => {},
  },
  setUser: () => {},
  clearUser: () => {},
  login,
  logout,
  refetch: () => {},
  activateMaintenanceMode: () => {},
  isProcessingLogin: false,
  setProcessingLogin: () => {},
  sitecoreContent: {},
  isAuth0Initializing: true,
  setIsAuth0Initializing: () => {},
});

const SuspendedWrapper: FC<{
  configReader: Function;
  fetchNewAppConfig: Function;
  language: string;
  delocalizedPathIn: string;
}> = ({
  configReader,
  fetchNewAppConfig,
  language: pageContextLanguage,
  delocalizedPathIn,
  children,
}) => {
  const data: AppConfig = configReader();
  const { user: fetchedUser = { login: '', isLoggedIn: false } } = data;
  const oktaWidget = useOktaWidget(fetchedUser);
  const [appConfig, setConfig] = useState<AppConfig>({
    ...data,
    user: fetchedUser,
  });
  const [isProcessingLogin, setProcessingLogin] = useState<boolean>(false);

  const [isAuth0Initializing, setIsAuth0Initializing] = useState(true);

  const {
    isMaintenanceMode,
    language: negotiatedLanguage,
    delocalizedPath,
    userAnalytics,
    localizedPath,
  } = appConfig;

  const user = data.user || appConfig.user;

  const { data: dominoData } = useDominoUserAccount(user?.individualId || '');

  // This may break react pages if DIS flow is enabled.
  // if (error) {
  //   throw Error('Error while fetching the domion Data');
  // }

  // On the error page, if a ?lang= param is present use it for Sitecore content
  // instead of the negotiated language, without triggering a URL redirect.
  const language = (() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const lang = params.get('lang');
      if (lang && window.location.pathname.includes('/error')) {
        // Accept-Language format: "en-US,en;q=0.9" — take the primary subtag of the first locale
        return lang
          .split(',')[0]
          .split(';')[0]
          .split('-')[0]
          .toLowerCase();
      }
    }
    return negotiatedLanguage;
  })();

  const userType = useSiteCoreUserType(user, isProcessingLogin);
  const sitecoreContent = useSitecoreLayout(userType, '/', language);

  const activateMaintenanceMode = () => {
    setConfig({ ...appConfig, isMaintenanceMode: false });
  };

  const refetch = () => {
    fetchNewAppConfig();
  };

  const setUser = (newUser: User) => {
    const dominoId = dominoData?.individualInfo?.id || null;
    setConfig({ ...appConfig, user: { ...newUser, dominoId } });
  };

  const clearUser = () => {
    setConfig({
      ...appConfig,
      user: {
        login: '',
        isLoggedIn: false,
      } as User,
    });
  };

  // trigger analytics on user change
  useUserTrackingEvent(appConfig, isProcessingLogin);
  // navigate to negotiated language
  useNavigateLanguage(appConfig, pageContextLanguage);

  useEffect(() => {
    if (
      (pageContextLanguage && data.language !== pageContextLanguage) ||
      delocalizedPath !== delocalizedPathIn
    ) {
      setConfig({
        ...data,
        user,
        delocalizedPath: delocalizedPathIn,
      });
    }
  }, [pageContextLanguage, delocalizedPathIn, data.language]);

  return (
    <AppConfigContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        user,
        isMaintenanceMode,
        isProcessingLogin,
        language,
        delocalizedPath,
        userAnalytics,
        localizedPath,
        login: (setCurrentPathAsDestination = false) => {
          return login(setCurrentPathAsDestination, language);
        },
        logout: (destination?: string) => {
          /* PECORE-42 change
          oktaWidget.clearTokens();
          */
          return logout(destination);
        },
        refetch,
        activateMaintenanceMode,
        setUser,
        clearUser,
        setProcessingLogin,
        oktaWidget,
        sitecoreContent,
        isAuth0Initializing,
        setIsAuth0Initializing,
      }}
    >
      {children}
    </AppConfigContext.Provider>
  );
};

export const useAppConfig: () => AppConfigContextType = () => {
  return useContext(AppConfigContext);
};

const AppConfigProvider: FC<{ language: string; path: string }> = ({
  language: pageContextLanguage,
  path: delocalizedPathIn,
  children,
}) => {
  defaultConfig.delocalizedPath = delocalizedPathIn;
  const domain = process.env.GATSBY_AUTH0_CUSTOM_DOMAIN as string;
  const clientId = process.env.GATSBY_AUTH0_CLIENT_ID as string;
  const client = useApolloClient();
  const {
    i18n: { language: currentLanguage },
  } = useTranslation();
  const fetchConfig = getAppConfig(
    client as ApolloClient<InMemoryCache>,
    currentLanguage,
    pageContextLanguage,
    delocalizedPathIn
  );

  const [fetchedAppConfig, fetchNewAppConfig] = useAsyncResource(fetchConfig);

  useMemo(() => {
    fetchNewAppConfig();
  }, [pageContextLanguage]);

  return (
    <ErrorBoundary>
      <Suspense fallback={<Loading />}>
        <SuspendedWrapper
          configReader={fetchedAppConfig}
          language={pageContextLanguage}
          delocalizedPathIn={delocalizedPathIn}
          fetchNewAppConfig={fetchNewAppConfig}
        >
          {isEnabled(FEATURE_AUTH0_LOGIN) ? (
            <Auth0Provider
              domain={domain || 'test.auth0.com'}
              clientId={clientId || 'test-client-id'}
              authorizationParams={{
                redirect_uri: window.location.origin,
              }}
              skipRedirectCallback={!isEnabled(FEATURE_AUTH0_LOGIN)}
            >
              {children}
            </Auth0Provider>
          ) : (
            children
          )}
        </SuspendedWrapper>
      </Suspense>
    </ErrorBoundary>
  );
};

export default AppConfigProvider;
